import { useEffect } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { logEvent } from 'firebase/analytics';

import Plans from '../../components/Plans/Plans';
import { Check } from '@mui/icons-material';
import { MyHelmet } from './MyHelmet';
import { analytics } from '../../config/firebase';

const FeaturesWrapper = ({ children }) => {
  return (
    <Box
      sx={{
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        position: 'relative',
        '&::before, &::after': {
          position: 'absolute',
          top: 0,
          width: '250px',
          height: '100%',
          content: '""',
          zIndex: '2',
        },
        '&::before': {
          left: { xs: -88, md: 0 },
          background:
            'linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 30%, rgba(255,255,255,0) 100%)',
        },
        '&::after': {
          right: { xs: -88, md: 0 },
          background:
            'linear-gradient(-90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 30%, rgba(255,255,255,0) 100%)',
        },
      }}
    >
      {children}
    </Box>
  );
};

const FeaturesTicker = ({ features, sx }) => {
  return (
    <Box
      component={'section'}
      sx={{
        '@keyframes ticker': {
          '0%': { transform: 'translateX(0)' },
          '100%': { transform: 'translateX(-100%)' },
        },
        animation: 'ticker 20s infinite linear',
        display: 'inline-block',
        ...sx,
      }}
    >
      <Stack direction={'row'} justifyContent={'center'} gap={4}>
        {features.map((feature, index) => (
          <Stack key={index} direction={'row'} gap={1} alignItems={'center'}>
            <Check color={'primary'} />
            <Typography variant={'body1'} color={'primary.neutral800'} noWrap>
              {feature}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const Faq = ({ faq }) => {
  return (
    <Stack gap={1} p={2}>
      <Typography
        variant="h2"
        color="primary.neutral700"
        sx={{ fontSize: '16px', fontWeight: '600' }}
      >
        {faq.QUESTION}
      </Typography>
      <Typography
        variant="body1"
        color="primary.neutral600"
        sx={{ fontSize: '15px', fontWeight: '400' }}
      >
        {faq.ANSWER}
      </Typography>
    </Stack>
  );
};

export default function Pricing() {
  const { t } = useTranslation();
  const features = t('PRICING.SERVICE_FEATURES', { returnObjects: true }) || [];
  const faq = t('FAQ_PRICING', { returnObjects: true }) || [];

  // Firebase analytics
  useEffect(() => {
    logEvent(analytics, 'page_view', {
      page_title: 'Pricing',
      page_location: window.location.href,
      page_path: window.location.pathname,
    });
  }, []);
  return (
    <>
      <MyHelmet />
      <Container
        maxWidth={'lg'}
        sx={{ py: 4, display: 'flex', gap: 2, flexDirection: 'column' }}
      >
        <Stack component={'section'} justifyContent={'center'} gap={2}>
          <Box component={'header'}>
            <Typography
              variant="h1"
              color="primary.neutral800"
              sx={{
                fontSize: '32px',
                fontWeight: '700',
                textTransform: 'uppercase',
                textAlign: 'center',
              }}
            >
              {t('PRICING.TITLE')}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            color="primary.neutral500"
            sx={{
              width: { xs: '100%', md: '60%' },
              mx: 'auto',
              textAlign: 'center',
            }}
          >
            {t('PRICING.DESCRIPTION')}
          </Typography>
          <Plans />
        </Stack>

        <Stack
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 10,
            py: 6,
          }}
        >
          <FeaturesWrapper>
            <FeaturesTicker features={features} />
            <FeaturesTicker features={features} sx={{ ml: 4 }} />
          </FeaturesWrapper>
          <Stack
            component={'section'}
            direction={{ xs: 'column', md: 'row' }}
            justifyContent={'space-between'}
            sx={{ px: { xs: 0, md: 8 } }}
            gap={{ xs: 0, md: 4 }}
          >
            <Stack gap={1}>
              {faq.slice(0, Math.ceil(faq.length / 2)).map((faq, index) => (
                <Stack key={index} gap={1}>
                  <Faq faq={faq} />
                </Stack>
              ))}
            </Stack>
            <Stack gap={1}>
              {faq.slice(0, Math.ceil(faq.length / 2)).map((faq, index) => (
                <Stack key={index} gap={1}>
                  <Faq faq={faq} />
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </>
  );
}
